import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { ARTICLES, getArticle } from "../data/blog";
import { Reveal } from "../components/Reveal";

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2 className="mt-14 mb-5 font-serif text-3xl sm:text-4xl font-light text-ink leading-snug">{block.text}</h2>;
    case "h3":
      return <h3 className="mt-10 mb-4 font-serif text-2xl text-ink">{block.text}</h3>;
    case "list":
      return (
        <ul className="my-6 space-y-3">
          {block.items.map((it, i) => (
            <li key={i} className="flex items-start gap-3 text-sage leading-relaxed">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-olive" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote className="my-10 border-l-2 border-gold pl-6 font-serif text-2xl font-light italic text-olive-dark leading-relaxed">
          « {block.text} »
        </blockquote>
      );
    case "cta":
      return (
        <div className="my-12 rounded-3xl border border-olive-border bg-olive-soft/40 p-8 text-center">
          <p className="text-ink leading-relaxed max-w-xl mx-auto">{block.text}</p>
          <Link
            to={block.to}
            data-testid="blog-article-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-olive px-8 py-4 text-sm font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark hover:-translate-y-0.5"
          >
            {block.label} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      );
    default:
      return <p className="my-5 text-base sm:text-lg text-sage leading-relaxed">{block.text}</p>;
  }
};

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    if (!article) return;
    document.title = article.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", article.metaDescription);

    const data = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.metaDescription,
      "image": `https://juliezittel.com${article.cover}`,
      "datePublished": article.date,
      "dateModified": article.date,
      "author": { "@type": "Person", "name": "Julie Zittel", "url": "https://juliezittel.com" },
      "publisher": { "@type": "Person", "name": "Julie Zittel" },
      "mainEntityOfPage": `https://juliezittel.com/blog/${article.slug}`,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "blog-jsonld";
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => { document.getElementById("blog-jsonld")?.remove(); };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const others = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <div data-testid="blog-article-page">
      {/* HERO */}
      <section className="pt-32 lg:pt-40 pb-10 px-6 lg:px-12 max-w-3xl mx-auto">
        <Reveal>
          <Link to="/blog" data-testid="blog-article-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-olive hover:text-olive-dark transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Le Journal
          </Link>
          <div className="mt-8 flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-olive">
            <span>{article.dateLabel}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {article.readingTime}</span>
          </div>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink leading-[1.1]">{article.title}</h1>
          <p className="mt-6 text-lg text-sage leading-relaxed">{article.excerpt}</p>
        </Reveal>
      </section>

      <section className="px-6 lg:px-12 max-w-4xl mx-auto">
        <Reveal>
          <div className="overflow-hidden rounded-3xl border border-olive-border">
            <img src={article.cover} alt={article.coverAlt} className="w-full h-64 sm:h-96 object-cover" />
          </div>
        </Reveal>
      </section>

      {/* CORPS */}
      <article className="px-6 lg:px-12 max-w-3xl mx-auto pb-8" data-testid="blog-article-body">
        {article.content.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </article>

      {/* SIGNATURE */}
      <section className="px-6 lg:px-12 max-w-3xl mx-auto pb-16">
        <div className="rounded-3xl border border-olive-border bg-beige-50 p-8 flex items-center gap-5">
          <img src="/images/portrait-cnt.jpg" alt="Julie Zittel, thérapeute et formatrice" loading="lazy" className="h-16 w-16 rounded-full object-cover" />
          <div>
            <p className="font-serif text-xl text-ink">Julie Zittel</p>
            <p className="text-sm text-sage">Thérapeute & formatrice depuis plus de 25 ans, à Bayonne.</p>
          </div>
        </div>
      </section>

      {/* AUTRES ARTICLES */}
      {others.length > 0 && (
        <section className="px-6 lg:px-12 max-w-7xl mx-auto pb-24">
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-8">À lire aussi</p>
          <div className="grid md:grid-cols-2 gap-8">
            {others.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`blog-related-${a.slug}`} className="group block overflow-hidden rounded-3xl border border-olive-border bg-beige-50 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_25px_50px_-25px_rgba(49,63,21,0.35)]">
                <img src={a.cover} alt={a.coverAlt} loading="lazy" className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-ink group-hover:text-olive transition-colors duration-300 leading-snug">{a.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-olive">Lire l'article <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
